import { z } from "zod";
import { MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH } from "@common/constants/domains/accounting/schedules";
import { accountingLedgerBasisSchema } from "@common/schemas/domains/accounting/ledger.schema";
import { dbIntIdSchema } from "@common/schemas/shared.schema";
import {
    ScheduleApplicationPeriodRole,
    ScheduleBillingStatus,
    ScheduleLifecycleFilterStatus,
} from "@common/types/domains/accounting/schedule";
import {
    scheduleApplicationMilestoneBaseSchema,
    scheduleApplicationPeriodRoleSchema,
    revenueSubModeSchema,
    expenseSubModeSchema,
    scheduleApplicationPeriodStatusSchema,
    scheduleApplicationStatusSchema,
    scheduleJeSubtypeSchema,
    scheduleSourceTypeSchema,
    scheduleTermUnitSchema,
    scheduleTypeSchema,
} from "./schedule.schema";

export const scheduleApplicationListJournalEntrySchema = z.object({
    id: z.string().uuid(),
    referenceNumber: z.string(),
    dateOnly: z.string().nullable(),
    description: z.string(),
    /** Ladder generation: apply writes 0, each change posts the next one. */
    revision: z.number().int(),
    amount: z.number(),
    /** Other account on the holding line, matching the ledger Split column. */
    splitLabel: z.string().nullable(),
});

export type ScheduleApplicationListJournalEntry = z.infer<
    typeof scheduleApplicationListJournalEntrySchema
>;

export const scheduleApplicationLifecycleJournalEntrySchema =
    scheduleApplicationListJournalEntrySchema.extend({
        subtype: scheduleJeSubtypeSchema,
        periodId: z.string().uuid().nullable(),
        /** Role of the entry's own period row; null for a legacy period-less entry. */
        periodRole: scheduleApplicationPeriodRoleSchema.nullable(),
        /** Reversals written by the period protocol: the row they cancel. */
        reversesPeriodId: z.string().uuid().nullable(),
        amount: z.number(),
        reversedSubtype: scheduleJeSubtypeSchema.nullable(),
    });

export type ScheduleApplicationLifecycleJournalEntry = z.infer<
    typeof scheduleApplicationLifecycleJournalEntrySchema
>;

/** The plan behind a milestone row; null on a calendar period. */
export const scheduleApplicationListMilestoneSchema =
    scheduleApplicationMilestoneBaseSchema.pick({
        sortOrder: true,
        name: true,
        allocationPercent: true,
        plannedAmount: true,
        expectedDate: true,
        postingDate: true,
        isOverRecognized: true,
    });

export type ScheduleApplicationListMilestone = z.infer<
    typeof scheduleApplicationListMilestoneSchema
>;

export const scheduleApplicationListPeriodSchema = z.object({
    id: z.string().uuid(),
    periodKey: z.string(),
    effectiveDate: z.string(),
    amount: z.number(),
    componentOrder: z.number().int(),
    subtype: scheduleJeSubtypeSchema,
    status: scheduleApplicationPeriodStatusSchema,
    /** Set on a milestone row; absent or null on a calendar period. */
    milestone: scheduleApplicationListMilestoneSchema.nullable().optional(),
    /** Ladder generation of the row. Apply writes 0; each change bumps it. */
    revision: z.number().int(),
    /** The list holds the month rows only; reversal and lifecycle rows are entries. */
    role: z.literal(ScheduleApplicationPeriodRole.ladder),
    /** Set once a reverse-mode change cancelled this month's entry. */
    reversedByPeriodId: z.string().uuid().nullable(),
    /** The live recognition: the latest generation. */
    journalEntry: scheduleApplicationListJournalEntrySchema.nullable(),
    /** Every recognition ever posted for the month, oldest generation first. */
    journalEntries: z
        .array(scheduleApplicationListJournalEntrySchema)
        .default([]),
});

export type ScheduleApplicationListPeriod = z.infer<
    typeof scheduleApplicationListPeriodSchema
>;

/** `source.label` of an invoice-sourced schedule whose invoice has no number. */
export const SCHEDULE_INVOICE_SOURCE_FALLBACK_LABEL = "Invoice";
/** `source.label` of a bill-sourced schedule whose bill has no number. */
export const SCHEDULE_BILL_SOURCE_FALLBACK_LABEL = "Bill";

export const scheduleApplicationSourceSummarySchema = z.object({
    sourceType: scheduleSourceTypeSchema,
    transactionId: z.number().int().nullable(),
    invoiceId: z.string().uuid().nullable(),
    billId: z.string().uuid().nullable(),
    /** Invoice or bill number, bank description or manual label; see the *_SOURCE_FALLBACK_LABEL constants. */
    label: z.string(),
    date: z.string().nullable(),
    amount: z.number().nullable(),
});

export type ScheduleApplicationSourceSummary = z.infer<
    typeof scheduleApplicationSourceSummarySchema
>;

export const scheduleApplicationSourceLineSchema = z.object({
    id: z.string().uuid(),
    description: z.string(),
    amount: z.number(),
    splitLabel: z.string().nullable(),
});

export type ScheduleApplicationSourceLine = z.infer<
    typeof scheduleApplicationSourceLineSchema
>;

export const scheduleApplicationRelatedLinkSchema = z.object({
    type: z.enum(["invoice", "bill", "transaction"]),
    entityId: z.number().int(),
    invoiceId: z.string().uuid().nullable(),
    billId: z.string().uuid().nullable(),
    transactionId: z.number().int().nullable(),
    label: z.string(),
    date: z.string().nullable(),
    amount: z.number(),
    counterpartyName: z.string().nullable(),
});

export type ScheduleApplicationRelatedLink = z.infer<
    typeof scheduleApplicationRelatedLinkSchema
>;

export const scheduleApplicationListCounterpartySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});

export const scheduleApplicationListAccountSchema = z.object({
    id: z.string().uuid(),
    code: z.number().int(),
    name: z.string(),
});

export const scheduleApplicationListItemSchema = z.object({
    id: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    scheduleTemplateName: z.string(),
    group: z.string(),
    scheduleType: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullable(),
    expenseSubMode: expenseSubModeSchema.nullable(),
    entityId: z.number().int(),
    sourceType: scheduleSourceTypeSchema,
    sourceLineIds: z.array(z.string().uuid()).nullable(),
    status: scheduleApplicationStatusSchema,
    amount: z.number(),
    termStart: z.string(),
    /** The application's own term once a change set one, else the template's. */
    termLength: z.number().int().positive(),
    termUnit: scheduleTermUnitSchema,
    specificDate: z.string().nullable(),
    ledgerId: z.string().uuid(),
    ledgerBasis: accountingLedgerBasisSchema,
    counterparty: scheduleApplicationListCounterpartySchema.nullable(),
    // Inherited from the primary source; a schedule template carries no class.
    classIds: z.array(z.string().uuid()).default([]),
    splitAccount: scheduleApplicationListAccountSchema.nullable(),
    /** The policy's recognition account for this schedule's ledger. */
    recognitionAccount: scheduleApplicationListAccountSchema.nullable(),
    /** Holding-line split of the source entry. Blank when the source has no entry. */
    splitLabel: z.string().nullable(),
    recognizedAmount: z.number(),
    remainingAmount: z.number(),
    /** Sum of every billing on a manual accrued schedule; 0 for any other. */
    billedAmount: z.number(),
    /** Null for anything but a live accrued revenue schedule. */
    billingStatus: z.nativeEnum(ScheduleBillingStatus).nullable(),
    /** Latest billed date across the schedule's billings. */
    lastBilledDate: z.string().date().nullable(),
    billingCount: z.number().int(),
    source: scheduleApplicationSourceSummarySchema,
    relatedLinks: z.array(scheduleApplicationRelatedLinkSchema).default([]),
    creationJournalEntry: scheduleApplicationListJournalEntrySchema.nullable(),
    /** The entry Kick posted for the source itself (invoice, transaction, bill). */
    sourceJournalEntry: scheduleApplicationListJournalEntrySchema.nullable(),
    lifecycleJournalEntries: z
        .array(scheduleApplicationLifecycleJournalEntrySchema)
        .default([]),
    isModified: z.boolean(),
    /** Rows are confirmed milestones, not calendar periods. */
    isMilestone: z.boolean(),
    /** Invoice line slices when one application covers multiple line items. */
    sourceLines: z.array(scheduleApplicationSourceLineSchema).optional(),
    periods: z.array(scheduleApplicationListPeriodSchema),
});

export type ScheduleApplicationListItem = z.infer<
    typeof scheduleApplicationListItemSchema
>;

export const listScheduleApplicationsResponseSchema = z.object({
    applications: z.array(scheduleApplicationListItemSchema),
});

export type ListScheduleApplicationsResponse = z.infer<
    typeof listScheduleApplicationsResponseSchema
>;

export const SCHEDULE_APPLICATIONS_LIST_MAX_LIMIT = 200;

// A single repeated query param arrives as a scalar; several arrive as an array.
function queryArray<T extends z.ZodTypeAny>(item: T) {
    return z.preprocess(
        (value) =>
            Array.isArray(value) || value === undefined ? value : [value],
        z.array(item),
    );
}

export const listWorkspaceScheduleApplicationsQuerySchema = z
    .object({
        templateIds: queryArray(z.string().uuid()).optional(),
        groups: queryArray(
            z.string().min(1).max(MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH),
        ).optional(),
        sourceTypes: queryArray(scheduleSourceTypeSchema).optional(),
        scheduleTypes: queryArray(scheduleTypeSchema).optional(),
        revenueSubModes: queryArray(revenueSubModeSchema).optional(),
        expenseSubModes: queryArray(expenseSubModeSchema).optional(),
        entityIds: queryArray(dbIntIdSchema).optional(),
        lifecycleStatuses: queryArray(
            z.nativeEnum(ScheduleLifecycleFilterStatus),
        ).optional(),
        billingStatuses: queryArray(
            z.nativeEnum(ScheduleBillingStatus),
        ).optional(),
        counterpartyNames: queryArray(z.string().min(1)).optional(),
        lineItemLabels: queryArray(z.string().min(1)).optional(),
        counterpartyIds: queryArray(z.string().uuid()).optional(),
        invoiceIds: queryArray(z.string().uuid()).optional(),
        transactionIds: queryArray(dbIntIdSchema).optional(),
        classIds: queryArray(z.string().uuid()).optional(),
        periodStart: z.string().date().optional(),
        periodEnd: z.string().date().optional(),
        search: z.string().max(255).optional(),
        /** Deep link from a transaction or invoice: show only that schedule. */
        applicationId: z.string().uuid().optional(),
        /** Deep link from a report cell: show only the schedules behind it. */
        applicationIds: queryArray(z.string().uuid()).optional(),
        // Both omitted = the full matching set; the list page passes both.
        page: z.coerce.number().int().min(1).optional(),
        limit: z.coerce
            .number()
            .int()
            .min(1)
            .max(SCHEDULE_APPLICATIONS_LIST_MAX_LIMIT)
            .optional(),
    })
    .refine(
        (value) => (value.page === undefined) === (value.limit === undefined),
        { message: "page and limit must be provided together" },
    );

export type ListWorkspaceScheduleApplicationsQuery = z.infer<
    typeof listWorkspaceScheduleApplicationsQuerySchema
>;

export const listWorkspaceScheduleApplicationsResponseSchema = z.object({
    applications: z.array(scheduleApplicationListItemSchema),
    total: z.number().int(),
    pageCount: z.number().int(),
    facets: z.object({
        /** Every counterparty with a schedule in the workspace, regardless of the active filters. */
        counterpartyNames: z.array(z.string()),
        lineItemLabels: z.array(z.string()),
    }),
});

export type ListWorkspaceScheduleApplicationsResponse = z.infer<
    typeof listWorkspaceScheduleApplicationsResponseSchema
>;
